<template>
  <div class="resolve-actions relative flex items-center justify-end">
    <div class="bot-status">
      <button
        class="bot-status-container"
        :class="buttonClass"
        v-tooltip="botTooltipMsg"
      >
        <fluent-icon type= "solid" icon="robot" size="16"/>
      </button>
    </div>
    <div class="conversation-status">
      <woot-button
        class-names="status-button"
        icon-type="solid"
        :color-scheme="buttonClass"
        :icon="buttonIcon"
        :emoji="buttonEmoji"
        :is-loading="isLoading"
        @click="openDropdown"
      >
        <span class="button__content-wrapper">
          <span class="button__name-group">
            <span class="button__name">{{ buttonName }}</span>
            <span v-if="isSnoozed" class="snoozed--display-text">{{ snoozedDisplayText }}</span>
          </span>
          <span class="dropdown-caret">
            <svg xmlns="http://www.w3.org/2000/svg" width="9px" height="14px" viewBox="0 0 320 512">]
              <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" fill="currentColor"/>
            </svg>
          </span>
        </span>
      </woot-button>
      <div
        v-if="showActionsDropdown"
        v-on-clickaway="closeDropdown"
        class="dropdown-pane dropdown-pane--open"
      >
        <woot-dropdown-menu>
          <woot-dropdown-item v-if="!isPending">
            <woot-button
              variant="clear"
              color-scheme="secondary"
              size="small"
              icon="snooze"
              @click="() => openSnoozeModal()"
            >
              {{ this.$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL') }}
            </woot-button>
          </woot-dropdown-item>
          <woot-dropdown-item v-if="!isOpen">
            <woot-button
              variant="clear"
              color-scheme="primary"
              size="tiny"
              icon="comment"
              icon-type="solid"
              @click="() => toggleStatus(STATUS_TYPE.OPEN)"
            >
              <span class="status-option__name">
                {{ this.$t('CONVERSATION.STATUS_DROPDOWN.OPEN') }}
              </span>
            </woot-button>
          </woot-dropdown-item>
          <woot-dropdown-item v-if="!isPending">
            <woot-button
              variant="clear"
              color-scheme="warning"
              size="tiny"
              icon="auto-icon"
              icon-type="solid"
              @click="() => toggleStatus(STATUS_TYPE.PENDING)"
            >
              <span class="status-option__name">
                {{ this.$t('CONVERSATION.STATUS_DROPDOWN.AUTOMATED') }}
              </span>
            </woot-button>
          </woot-dropdown-item>
          <woot-dropdown-item v-if="!isResolved && !isPending">
            <woot-button
              variant="clear"
              color-scheme="success"
              size="tiny"
              icon="check-mark-circle"
              icon-type="solid"
              @click="() => toggleStatus(STATUS_TYPE.RESOLVED)"
            >
              <span class="status-option__name">
                {{ this.$t('CONVERSATION.STATUS_DROPDOWN.RESOLVED') }}
              </span>
            </woot-button>
          </woot-dropdown-item>
        </woot-dropdown-menu>
      </div>
    </div>
    <woot-modal
      :show.sync="showCustomSnoozeModal"
      :on-close="hideCustomSnoozeModal"
    >
      <custom-snooze-modal
        @close="hideCustomSnoozeModal"
        @choose-time="chooseSnoozeTime"
      />
    </woot-modal>
  </div>
</template>

<script>
import { getUnixTime } from 'date-fns';
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';
import CustomSnoozeModal from 'dashboard/components/CustomSnoozeModal';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import {
  hasPressedAltAndEKey,
  hasPressedCommandPlusAltAndEKey,
  hasPressedAltAndMKey,
} from 'shared/helpers/KeyboardHelpers';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import differenceInHours from 'date-fns/differenceInHours';

import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
  CMD_SNOOZE_CONVERSATION,
} from '../../routes/dashboard/commands/commandBarBusEvents';

export default {
  components: {
    WootDropdownItem,
    WootDropdownMenu,
    CustomSnoozeModal,
  },
  mixins: [clickaway, alertMixin, eventListenerMixins],
  props: { conversationId: { type: [String, Number], required: true } },
  data() {
    return {
      isLoading: false,
      showActionsDropdown: false,
      STATUS_TYPE: wootConstants.STATUS_TYPE,
      showCustomSnoozeModal: false,
    };
  },
  computed: {
    ...mapGetters({ currentChat: 'getSelectedChat' }),
    isOpen() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.OPEN;
    },
    isPending() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.PENDING;
    },
    isResolved() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.RESOLVED;
    },
    isSnoozed() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    buttonClass() {
      if (this.isPending) return 'warning';
      if (this.isOpen) return 'primary';
      if (this.isResolved) return 'success';
      if (this.isSnoozed) return 'snoozed';
      return '';
    },
    buttonIcon() {
      if (this.isPending) return 'auto-icon';
      if (this.isOpen) return 'comment';
      if (this.isResolved) return 'check-mark-circle';
      if (this.isSnoozed) return 'clock';
      return '';
    },
    buttonEmoji() {
      if (this.isPending) return '🤖';
      if (this.isOpen) return '💬';
      if (this.isResolved) return '✅';
      if (this.isSnoozed) return '🕗';
      return '';
    },
    showAdditionalActions() {
      return !this.isPending && !this.isSnoozed;
    },
    buttonName() {
      if (this.isPending) return this.$t('CONVERSATION.HEADER.AUTOMATED');
      if (this.isOpen) return this.$t('CONVERSATION.HEADER.ESCALATED');
      if (this.isResolved) return this.$t('CONVERSATION.HEADER.RESOLVED');
      if (this.isSnoozed) return this.$t('CONVERSATION.HEADER.SNOOZED');
      return '';
    },
    botTooltipMsg() {
      if (this.isPending) return this.$t('CONVERSATION.BOT_STATUS.ACTIVELY_HANDLING');
      if (this.isResolved) return this.$t('CONVERSATION.BOT_STATUS.ACTIVELY_HANDLING');
      if (this.isOpen || this.isSnoozed) return this.$t('CONVERSATION.BOT_STATUS.ONLY_SUGGESTING');
      return '';
    },
    snoozedDisplayText() {
      const { snoozed_until: snoozedUntil } = this.currentChat;
      if (snoozedUntil) {
        // When the snooze is applied, it schedules the unsnooze event to next day/week 9AM.
        // By that logic if the time difference is less than or equal to 24 + 9 hours we can consider it tomorrow.
        const MAX_TIME_DIFFERENCE = 33;
        const isSnoozedUntilTomorrow =
          differenceInHours(new Date(snoozedUntil), new Date()) <=
          MAX_TIME_DIFFERENCE;
        return this.$t(
          isSnoozedUntilTomorrow
            ? 'CONVERSATION.HEADER.SNOOZED_UNTIL_TOMORROW'
            : 'CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_WEEK'
        );
      }
      return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
    }
  },
  mounted() {
    bus.$on(CMD_SNOOZE_CONVERSATION, this.onCmdSnoozeConversation);
    bus.$on(CMD_REOPEN_CONVERSATION, this.onCmdOpenConversation);
    bus.$on(CMD_RESOLVE_CONVERSATION, this.onCmdResolveConversation);
  },
  destroyed() {
    bus.$off(CMD_SNOOZE_CONVERSATION, this.onCmdSnoozeConversation);
    bus.$off(CMD_REOPEN_CONVERSATION, this.onCmdOpenConversation);
    bus.$off(CMD_RESOLVE_CONVERSATION, this.onCmdResolveConversation);
  },
  methods: {
    async handleKeyEvents(e) {
      const allConversations = document.querySelectorAll(
        '.conversations-list .conversation'
      );
      if (hasPressedAltAndMKey(e)) {
        if (this.$refs.arrowDownButton) {
          this.$refs.arrowDownButton.$el.click();
        }
      }
      if (hasPressedAltAndEKey(e)) {
        const activeConversation = document.querySelector(
          'div.conversations-list div.conversation.active'
        );
        const activeConversationIndex = [...allConversations].indexOf(
          activeConversation
        );
        const lastConversationIndex = allConversations.length - 1;
        try {
          await this.toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
        } catch (error) {
          // error
        }
        if (hasPressedCommandPlusAltAndEKey(e)) {
          if (activeConversationIndex < lastConversationIndex) {
            allConversations[activeConversationIndex + 1].click();
          } else if (allConversations.length > 1) {
            allConversations[0].click();
            document.querySelector('.conversations-list').scrollTop = 0;
          }
          e.preventDefault();
        }
      }
    },
    onCmdSnoozeConversation(snoozeType) {
      if (snoozeType === wootConstants.SNOOZE_OPTIONS.UNTIL_CUSTOM_TIME) {
        this.showCustomSnoozeModal = true;
      } else {
        this.toggleStatus(
          this.STATUS_TYPE.SNOOZED,
          findSnoozeTime(snoozeType) || null
        );
      }
    },
    chooseSnoozeTime(customSnoozeTime) {
      this.showCustomSnoozeModal = false;
      if (customSnoozeTime) {
        this.toggleStatus(
          this.STATUS_TYPE.SNOOZED,
          getUnixTime(customSnoozeTime)
        );
      }
    },
    hideCustomSnoozeModal() {
      this.showCustomSnoozeModal = false;
    },
    onCmdOpenConversation() {
      this.toggleStatus(this.STATUS_TYPE.OPEN);
    },
    onCmdResolveConversation() {
      this.toggleStatus(this.STATUS_TYPE.RESOLVED);
    },
    showOpenButton() {
      return this.isResolved || this.isSnoozed;
    },
    closeDropdown() {
      this.showActionsDropdown = false;
    },
    openDropdown() {
      this.showActionsDropdown = true;
    },
    toggleStatus(status, snoozedUntil) {
      this.closeDropdown();
      this.isLoading = true;
      this.$store
        .dispatch('toggleStatus', {
          conversationId: this.currentChat.id,
          status,
          snoozedUntil,
        })
        .then(() => {
          this.showAlert(this.$t('CONVERSATION.CHANGE_STATUS'));
          this.isLoading = false;
        });
    },
    openSnoozeModal() {
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'snooze_conversation' });
    },
  },
};
</script>
<style lang="scss" scoped>
.conversation-status {
  .status-button {
    border: 1px solid #DDE6EE;
    background-color: #FFF;
    border-radius: 25px;
    font-weight: 600;

    &:hover,
    &:focus {
      background-color: #FFF;
      border: 1px solid #37546D;
    }
    .button__content-wrapper {
      display: flex;
      align-items: center;
      text-align: left;

      .button__name-group {
        display: inline-block;
        text-align: left;
        min-width: 78px;
      }
      .dropdown-caret {
        color: #37546D;

        svg {
          vertical-align: top;
          margin-left: 10px;
        }
      }
    }

    &.primary { color: #1f93ff; }
    &.warning { color: #ffc532; }
    &.success { color: #44ce4b; }
    &.snoozed {
      color: #37546D;

      .button__name-group {
        display: inline-flex;
        flex-flow: column;
      }
      .snoozed--display-text {
        font-size: 10px;
        font-weight: 600;
        line-height: 1.2;
        color: #93AFC8;
      }
    }
  }
}

.dropdown-pane {
  @apply left-auto top-[2.625rem] mt-0.5 right-0 max-w-[12.5rem] min-w-[9.75rem];
  .dropdown-menu__item {
    @apply mb-0;
  }
  .dropdown-menu--header .title {
    color: #93AFC8;
    font-weight: 600;
    }

  .status-option__name {
    font-size: 12px;
    font-weight: 600;
    margin-left: 8px;
  }

  .dropdown-menu__item {
    .button.clear.warning {
      color: #ffc532;
    }
  }
}

.bot-status {
  padding-left: 16px;
  border-left: 1px solid #EBF0F5;
  margin-right: 16px;
}

.bot-status-container {
  align-items: center;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding-top: 2px;
  height: 40px;
  width: 40px;

  &.primary,
  &.snoozed  {
    color: #ABC1D4;
    background-color: #DDE6EE;
  }
  &.warning {
    color: #1F93FF;
    background-color: #CCE6FF;
  }
  &.success {
    color: #30B537;
    background-color: #D7F4D8;
  }
}
</style>